import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type { AdmissionApplication, ApplicationStatus, ApproveApplicationPayload, AdmissionResult } from "../types";

const APPLICATIONS = INSTITUTION_ADMIN_ENDPOINTS.ADMISSIONS.APPLICATIONS;
const QR = INSTITUTION_ADMIN_ENDPOINTS.ADMISSIONS.QR;

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export async function fetchApplications(
  institutionId: number,
  params?: { status?: ApplicationStatus; page?: number; limit?: number }
): Promise<{ applications: AdmissionApplication[]; pagination: Pagination }> {
  const { data } = await privateClient.get(APPLICATIONS.LIST(institutionId), { params });
  return data;
}

export async function approveApplication(
  institutionId: number,
  applicationId: number,
  payload: ApproveApplicationPayload
): Promise<AdmissionResult> {
  const { data } = await privateClient.post(APPLICATIONS.APPROVE(institutionId, applicationId), payload);
  return data;
}

export async function rejectApplication(
  institutionId: number,
  applicationId: number,
  rejectionReason?: string
): Promise<AdmissionApplication> {
  const { data } = await privateClient.post(APPLICATIONS.REJECT(institutionId, applicationId), { rejectionReason });
  return data.application;
}

export async function recordRegistrationFee(
  institutionId: number,
  applicationId: number,
  payload: { paymentMode?: string; referenceNo?: string; note?: string; waive?: boolean; bankAccountId?: number }
): Promise<AdmissionApplication> {
  const { data } = await privateClient.post(APPLICATIONS.REGISTRATION_FEE(institutionId, applicationId), payload);
  return data.application;
}

export async function fetchQrInfo(institutionId: number): Promise<{ token: string; applyUrl: string }> {
  const { data } = await privateClient.get(QR.INFO(institutionId));
  return data;
}

// Downloads the branded QR PNG as a Blob for the caller to trigger a save.
export async function downloadQr(institutionId: number): Promise<Blob> {
  const { data } = await privateClient.get(QR.DOWNLOAD(institutionId), { responseType: "blob" });
  return data;
}

// Same image, but via the broader-access preview endpoint — for showing
// the QR inline rather than saving it (see admissionFormRoutes.js: preview
// is MANAGE_SCOPES, download stays institution-admin-only).
export async function previewQr(institutionId: number): Promise<Blob> {
  const { data } = await privateClient.get(QR.PREVIEW(institutionId), { responseType: "blob" });
  return data;
}
